/**
 * Admin API Client
 * КРИТИЧНО: Используйте ТОЛЬКО endpoints из A1_API_INVENTORY.json
 * 35 endpoints для Admin Panel
 */

import api from "./api";
import { ApiEnvelope, unwrap } from "./envelope";
import type { User, Role, Permission, PaginatedResponse } from "../../types";
import type {
  AdminUsersResponse,
  SystemHealth,
  SystemMetrics,
  AuditLog,
  AdminSettings,
  FeatureFlag,
  ModerationQueueItem,
  ModerationStats,
  PerformanceMetrics,
  DatabaseStats,
  UserReport,
  SystemReport,
} from "../../types/admin.types";
import type {
  AdminMobileDashboard,
  AdminMobileQuickStatsData,
  AdminSecurityStats,
  AdminSecurityIncidentsData,
  AdminSecuritySettings,
  AdminSecuritySettingsUpdate,
  AdminBackupStats,
  AdminBackupSchedule,
  AdminBackupScheduleUpdate,
  AdminSecurityQuery,
} from "../../types/admin";

/**
 * Admin API
 * КРИТИЧНО: Все endpoints соответствуют A1_API_INVENTORY.json
 */
export const adminApi = {
  // ============================================================================
  // USER MANAGEMENT (12 endpoints)
  // ============================================================================

  /**
   * Список пользователей админки.
   * GET /api/admin/users
   *
   * AD-85. Конверт ADR-002 не снимался: `AdminUsersResponseDto` — объект с `success`,
   * но БЕЗ `data`, поэтому глобальный `ResponseInterceptor` бэкенда заворачивает его
   * целиком, и полезная нагрузка лежит на `response.data.data`. Метод возвращал
   * `response.data`, то есть сам конверт, а оба потребителя (`useAdminUsers` и
   * `AdminDashboard`) читают `usersData.users` — получали `undefined`, и таблица
   * показывала пусто ВСЕГДА, при любом содержимом базы. Дженерик axios на этом молчит:
   * `api.get<T>` объявляет `AxiosResponse<T>`, но кладёт в `.data` конверт. Тот же
   * класс ошибки, что P-3; распаковка — через общий `unwrap`, как в
   * `getSystemHealth`/`getSystemMetrics`.
   */
  getAllUsers: async (
    page = 1,
    limit = 20,
  ): Promise<{ data: AdminUsersResponse }> => {
    const res = await api.get<ApiEnvelope<AdminUsersResponse>>(
      `/admin/users?page=${page}&limit=${limit}`,
    );
    return { data: unwrap(res.data, "GET /admin/users") };
  },

  /**
   * Create user
   * POST /api/admin/users
   */
  createUser: (data: Partial<User>): Promise<{ data: User }> => {
    return api.post<User>("/admin/users", data);
  },

  /**
   * Delete user
   * DELETE /api/admin/users/:id
   */
  deleteUser: (id: string): Promise<void> => {
    return api.delete(`/admin/users/${id}`);
  },

  /**
   * Заблокировать пользователя.
   * POST /api/admin/users/:userId/action, тело { action: 'ban', reason }.
   *
   * AD-84. Прежде метод бил в `POST /admin/users/:id/ban` — такого маршрута на
   * бэкенде НЕТ ни под каким именем, то есть кнопка «Заблокировать» на /admin/users
   * возвращала 404 при каждом нажатии. Реализация существует, но по другому адресу:
   * один маршрут действий с перечислением в теле. Он же поднимает tokenVersion,
   * то есть выданные ранее токены заблокированного перестают действовать.
   */
  banUser: (
    id: string,
    reason: string,
    durationDays?: number,
  ): Promise<void> => {
    // AD-89. Срок уходит на сервер только когда он задан: поле `duration`
    // необязательное, и пустое значение означает «бессрочно». Слать `undefined`
    // безопасно (axios не сериализует), но условие оставлено явным — тело запроса
    // должно читаться так же, как читается диалог.
    return api.post(`/admin/users/${id}/action`, {
      action: "ban",
      reason,
      ...(durationDays ? { duration: durationDays } : {}),
    });
  },

  /**
   * Снять блокировку.
   * POST /api/admin/users/:userId/action, тело { action: 'unban' }.
   * AD-84: см. banUser — маршрута `/unban` на бэкенде не существует.
   */
  unbanUser: (id: string): Promise<void> => {
    return api.post(`/admin/users/${id}/action`, { action: "unban" });
  },

  /**
   * Отправить пользователю письмо со ссылкой для сброса пароля.
   * POST /api/admin/users/:userId/action, тело { action: 'reset_password' }.
   *
   * AD-87. До 05.09.2026 сервер на это действие отвечал «успешно выполнено», не
   * делая ничего: ветка была пустой. Теперь она идёт тем же путём, что и
   * «забыли пароль» у самого человека, и подтверждает ДОСТАВКУ письма — отказ
   * почтовой службы приходит 503-м, а не молчанием.
   */
  resetUserPassword: (id: string): Promise<void> => {
    return api.post(`/admin/users/${id}/action`, {
      action: "reset_password",
    });
  },

  /**
   * Get all roles
   * GET /api/admin/roles
   * КРИТИЧНО: BC-002 - возвращает Role[] (НЕ PaginatedResponse согласно A1)
   */
  getAllRoles: (): Promise<{ data: Role[] }> => {
    return api.get<Role[]>("/admin/roles");
  },

  /**
   * Get all permissions
   * GET /api/admin/permissions
   * КРИТИЧНО: BC-002 - возвращает Permission[] (НЕ PaginatedResponse согласно A1)
   */
  getAllPermissions: (): Promise<{ data: Permission[] }> => {
    return api.get<Permission[]>("/admin/permissions");
  },

  /**
   * Выдать пользователю внутреннюю роль.
   * POST /api/auth/assign-role, тело { userId, roleName }.
   *
   * AD-84. Прежде метод бил в `POST /admin/users/:userId/roles` с телом { roleId } —
   * такого маршрута на бэкенде нет, диалог «Назначить роль» отвечал 404. Настоящий
   * маршрут принимает ИМЯ роли (`AssignRoleDto.roleName`), а не идентификатор:
   * сервер сам находит строку словаря по имени. Операция добавляет роль, а не
   * заменяет прежние — у пользователя их может быть несколько (user_internal_roles).
   */
  assignRole: (userId: string, roleName: string): Promise<void> => {
    return api.post("/auth/assign-role", { userId, roleName });
  },

  // AD-88 (удалено 15.09.2026, решение Владимира). Здесь стояли `revokeRole` и
  // `getUserActivity`: маршрутов `DELETE /admin/users/:userId/roles/:roleId` и
  // `GET /admin/users/:id/activity` на бэкенде нет, вызывающих не было ни одного.
  // Отзыв роли наружу не выведен вовсе (`InternalRolesService` гасит `isActive` в
  // `user_internal_roles`, маршрута к этому нет) — когда операцию заведут на сервере,
  // метод пишется заново под настоящий маршрут, а не воскрешается отсюда.

  // ============================================================================
  // CONTENT MODERATION (8 endpoints)
  // ============================================================================

  /**
   * Get pending events
   * GET /api/admin/moderation/events?status=pending
   * КРИТИЧНО: BC-002 - возвращает PaginatedResponse
   */
  getPendingEvents: (
    page = 1,
    limit = 20,
  ): Promise<{ data: PaginatedResponse<any> }> => {
    return api.get<PaginatedResponse<any>>(
      `/admin/moderation/events?status=pending&page=${page}&limit=${limit}`,
    );
  },

  /**
   * Get pending comments
   * GET /api/admin/moderation/comments?status=pending
   * КРИТИЧНО: BC-002 - возвращает PaginatedResponse
   */
  getPendingComments: (
    page = 1,
    limit = 20,
  ): Promise<{ data: PaginatedResponse<any> }> => {
    return api.get<PaginatedResponse<any>>(
      `/admin/moderation/comments?status=pending&page=${page}&limit=${limit}`,
    );
  },

  /**
   * Approve event
   * POST /api/admin/moderation/events/:id/approve
   */
  approveEvent: (id: string): Promise<void> => {
    return api.post(`/admin/moderation/events/${id}/approve`);
  },

  /**
   * Reject event
   * POST /api/admin/moderation/events/:id/reject
   */
  rejectEvent: (id: string, reason: string): Promise<void> => {
    return api.post(`/admin/moderation/events/${id}/reject`, { reason });
  },

  /**
   * Approve comment
   * POST /api/admin/moderation/comments/:id/approve
   */
  approveComment: (id: string): Promise<void> => {
    return api.post(`/admin/moderation/comments/${id}/approve`);
  },

  /**
   * Reject comment
   * POST /api/admin/moderation/comments/:id/reject
   */
  rejectComment: (id: string, reason: string): Promise<void> => {
    return api.post(`/admin/moderation/comments/${id}/reject`, { reason });
  },

  /**
   * Get moderation queue
   * GET /api/admin/moderation/queue
   */
  getModerationQueue: (): Promise<{ data: ModerationQueueItem[] }> => {
    return api.get<ModerationQueueItem[]>("/admin/moderation/queue");
  },

  /**
   * Get moderation stats
   * GET /api/admin/moderation/stats
   */
  getModerationStats: (): Promise<{ data: ModerationStats }> => {
    return api.get<ModerationStats>("/admin/moderation/stats");
  },

  // ============================================================================
  // SYSTEM MONITORING (7 endpoints)
  // ============================================================================

  /**
   * Get system health
   * GET /api/admin/system/health
   *
   * Н-24-bis. Метод объявлял `AxiosResponse<SystemHealth>` и отдавал его как есть, а
   * потребители читали `.data` ОДИН раз — то есть получали конверт ADR-002
   * `{ success, data, meta }`, а не полезную нагрузку. Дженерик axios такое не ловит,
   * `tsc` был зелёным, экран рисовал пустоту. Тот же класс, что находка P-3
   * (`api-keys.api.ts`), — контракт и распаковка вынесены в `./envelope`.
   * Возвращаемый ТИП не меняется: `data` теперь честно содержит нагрузку, поэтому
   * ни один потребитель не правится.
   */
  getSystemHealth: async (): Promise<{ data: SystemHealth }> => {
    const res = await api.get<ApiEnvelope<SystemHealth>>(
      "/admin/system/health",
    );
    return { data: unwrap(res.data, "GET /admin/system/health") };
  },

  /**
   * Get system metrics
   * GET /api/admin/system/metrics
   *
   * Тот же конверт ADR-002, что и у health — см. комментарий выше.
   */
  getSystemMetrics: async (): Promise<{ data: SystemMetrics }> => {
    const res = await api.get<ApiEnvelope<SystemMetrics>>(
      "/admin/system/metrics",
    );
    return { data: unwrap(res.data, "GET /admin/system/metrics") };
  },

  /**
   * Get audit logs
   * GET /api/admin/audit-logs
   * КРИТИЧНО: BC-002 - возвращает PaginatedResponse<AuditLog>
   */
  getAuditLogs: (
    page = 1,
    limit = 20,
  ): Promise<{ data: PaginatedResponse<AuditLog> }> => {
    return api.get<PaginatedResponse<AuditLog>>(
      `/admin/audit-logs?page=${page}&limit=${limit}`,
    );
  },

  /**
   * Get error logs
   * GET /api/admin/error-logs
   * КРИТИЧНО: BC-002 - возвращает PaginatedResponse
   */
  getErrorLogs: (
    page = 1,
    limit = 20,
  ): Promise<{ data: PaginatedResponse<any> }> => {
    return api.get<PaginatedResponse<any>>(
      `/admin/error-logs?page=${page}&limit=${limit}`,
    );
  },

  /**
   * Get performance metrics
   * GET /api/admin/system/performance
   */
  getPerformanceMetrics: (): Promise<{ data: PerformanceMetrics }> => {
    return api.get<PerformanceMetrics>("/admin/system/performance");
  },

  /**
   * Get database stats
   * GET /api/admin/system/database
   */
  getDatabaseStats: (): Promise<{ data: DatabaseStats }> => {
    return api.get<DatabaseStats>("/admin/system/database");
  },

  /**
   * Clear cache
   * POST /api/admin/system/cache/clear
   */
  clearCache: (): Promise<void> => {
    return api.post("/admin/system/cache/clear");
  },

  // ============================================================================
  // CONFIGURATION (5 endpoints)
  // ============================================================================

  /**
   * Get system settings
   * GET /api/admin/settings
   */
  getSystemSettings: (): Promise<{ data: AdminSettings[] }> => {
    return api.get<AdminSettings[]>("/admin/settings");
  },

  /**
   * Update system settings
   * PATCH /api/admin/settings
   */
  updateSystemSettings: (
    settings: Record<string, unknown>,
  ): Promise<{ data: AdminSettings[] }> => {
    return api.patch<AdminSettings[]>("/admin/settings", settings);
  },

  /**
   * Get feature flags
   * GET /api/admin/feature-flags
   */
  getFeatureFlags: (): Promise<{ data: FeatureFlag[] }> => {
    return api.get<FeatureFlag[]>("/admin/feature-flags");
  },

  /**
   * Toggle feature flag
   * PATCH /api/admin/feature-flags/:flag
   */
  toggleFeatureFlag: (
    flag: string,
    enabled: boolean,
  ): Promise<{ data: FeatureFlag }> => {
    return api.patch<FeatureFlag>(`/admin/feature-flags/${flag}`, { enabled });
  },

  /**
   * Reset settings
   * POST /api/admin/settings/reset
   */
  resetSettings: (): Promise<void> => {
    return api.post("/admin/settings/reset");
  },

  // ============================================================================
  // REPORTS & ANALYTICS (3 endpoints)
  // ============================================================================

  /**
   * Generate user report
   * POST /api/admin/reports/users
   */
  generateUserReport: (
    filters: Record<string, unknown>,
  ): Promise<{ data: UserReport }> => {
    return api.post<UserReport>("/admin/reports/users", filters);
  },

  /**
   * Generate system report
   * POST /api/admin/reports/system
   */
  generateSystemReport: (
    filters: Record<string, unknown>,
  ): Promise<{ data: SystemReport }> => {
    return api.post<SystemReport>("/admin/reports/system", filters);
  },

  /**
   * Export data
   * GET /api/admin/export/:type
   * КРИТИЧНО: responseType: 'blob' для скачивания файла
   */
  exportData: (type: string): Promise<{ data: Blob }> => {
    return api.get(`/admin/export/${type}`, {
      responseType: "blob",
    });
  },

  // ============================================================================
  // SECURITY (4 endpoints)
  // ============================================================================

  /**
   * Get security stats
   * GET /api/admin/security/stats
   */
  getSecurityStats: (): Promise<AdminSecurityStats> => {
    return api
      .get<{
        success?: boolean;
        data?: AdminSecurityStats;
        message?: string;
      }>("/admin/security/stats")
      .then(
        (res) => res.data?.data ?? (res.data as unknown as AdminSecurityStats),
      );
  },

  /**
   * Get security incidents (alerts)
   * GET /api/admin/security/alerts
   */
  getSecurityIncidents: (
    params?: AdminSecurityQuery,
  ): Promise<AdminSecurityIncidentsData> => {
    return api
      .get<{
        success?: boolean;
        data?: AdminSecurityIncidentsData;
        message?: string;
      }>("/admin/security/alerts", {
        params: params ?? {},
      })
      .then(
        (res) =>
          res.data?.data ?? (res.data as unknown as AdminSecurityIncidentsData),
      );
  },

  /**
   * Get security settings
   * GET /api/admin/security/settings
   */
  getSecuritySettings: (): Promise<AdminSecuritySettings> => {
    return api
      .get<{
        success?: boolean;
        data?: AdminSecuritySettings;
        message?: string;
      }>("/admin/security/settings")
      .then(
        (res) =>
          res.data?.data ?? (res.data as unknown as AdminSecuritySettings),
      );
  },

  /**
   * Update security settings
   * PUT /api/admin/security/settings
   */
  updateSecuritySettings: (
    settings: AdminSecuritySettingsUpdate,
  ): Promise<AdminSecuritySettings> => {
    return api
      .put<{
        success?: boolean;
        data?: AdminSecuritySettings;
        message?: string;
      }>("/admin/security/settings", settings)
      .then(
        (res) =>
          res.data?.data ?? (res.data as unknown as AdminSecuritySettings),
      );
  },

  /**
   * Execute security action
   * POST /api/admin/security/actions
   */
  executeSecurityAction: (payload: {
    incidentId: string;
    action: string;
    reason: string;
    parameters?: Record<string, unknown>;
    comments?: string;
  }): Promise<void> => {
    return api.post("/admin/security/actions", payload);
  },

  // ============================================================================
  // BACKUP (3 endpoints)
  // ============================================================================

  /**
   * Get backup stats
   * GET /api/admin/backup/stats
   */
  getBackupStats: (): Promise<AdminBackupStats> => {
    return api
      .get<{
        success?: boolean;
        data?: AdminBackupStats;
        message?: string;
      }>("/admin/backup/stats")
      .then(
        (res) => res.data?.data ?? (res.data as unknown as AdminBackupStats),
      );
  },

  /**
   * Get backup schedule
   * GET /api/admin/backup/schedule
   */
  getBackupSchedule: (): Promise<AdminBackupSchedule> => {
    return api
      .get<{
        success?: boolean;
        data?: AdminBackupSchedule;
        message?: string;
      }>("/admin/backup/schedule")
      .then(
        (res) => res.data?.data ?? (res.data as unknown as AdminBackupSchedule),
      );
  },

  /**
   * Update backup schedule
   * PUT /api/admin/backup/schedule
   */
  updateBackupSchedule: (
    schedule: AdminBackupScheduleUpdate,
  ): Promise<AdminBackupSchedule> => {
    return api
      .put<{
        success?: boolean;
        data?: AdminBackupSchedule;
        message?: string;
      }>("/admin/backup/schedule", schedule)
      .then(
        (res) => res.data?.data ?? (res.data as unknown as AdminBackupSchedule),
      );
  },

  // ============================================================================
  // MOBILE DASHBOARD (2 endpoints)
  // ============================================================================

  /**
   * Get mobile dashboard
   * GET /api/admin/mobile/dashboard
   */
  getMobileDashboard: (): Promise<{
    success: boolean;
    data: AdminMobileDashboard;
    message?: string;
  }> => {
    return api.get("/admin/mobile/dashboard").then((res) => res.data);
  },

  /**
   * Get mobile quick stats
   * GET /api/admin/mobile/quick-stats
   */
  getMobileQuickStats: (): Promise<AdminMobileQuickStatsData> => {
    return api.get("/admin/mobile/quick-stats").then((res) => res.data);
  },
};
